import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Route,
  Routes,
  useNavigate,
  useSearchParams,
  Navigate,
  useLocation,
} from "react-router-dom";
import { MainStore } from "core/store";
import {
  PUBLIC_ROUTES,
  AUTH_ROUTES,
  COMMON_ROUTES,
  CADDY_ROUTES,
  GOLFER_ROUTES,
  QUERY_PARAMS,
  LOGIN_TYPES,
} from "constants/routesPath";
import api from "api";
import Home from "containers/home";
import RegisterCaddy from "containers/register/caddy";
import RegisterGolfer from "containers/register/golfer";
import Profile from "containers/profile";
import Login from "containers/login";
import LeaveSetup from "containers/caddy/leave-setup";
import SearchCaddy from "containers/golfer/search-caddy";
import CaddyJob from "containers/caddy/job";
import SearchCaddyView from "containers/golfer/search-caddy/view";
import LuckyDraw from "containers/caddy/lucky-draw";
import Favorite from "containers/golfer/favorite";
import Review from "containers/golfer/review";
import TipOfferList from "containers/golfer/tip-offer/list";
import TipOfferCreate from "containers/golfer/tip-offer/create";
import TipOfferDetail from "containers/golfer/tip-offer/detail";
import CaddyTipOffer from "containers/caddy/tip-offer";
import Loading from "components/loading";

export default function PrivateRoute({ isNotFoundUser }) {
  const [isReady, setIsReady] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [userState, setUserState] = useState({
    showAuthRoutes: true,
    userType: null,
  });
  const { userData, setUserData } = useContext(MainStore);
  const navigate = useNavigate();
  const location = useLocation();
  const [pageParams] = useSearchParams();
  const isDevelopment = process.env.REACT_APP_IS_DEVELOPMENT;
  const isCaddy = parseInt(pageParams.get("login_type")) === 0;
  const penddingList = [CADDY_ROUTES.LUCKY_DRAW];

  const SmartRedirect = ({ defaultPath }) => {
    if (Object.values(PUBLIC_ROUTES).includes(location.pathname)) {
      return null;
    }
    return <Navigate to={defaultPath} replace />;
  };

  const getUser = useCallback(
    async (retryCount = 0) => {
      const isLoggin = localStorage.getItem("isLogin") === "true";
      const maxRetries = 3;
      const retryDelay = 1000;

      if (!isLoggin || hasInitialized) return;

      try {
        const { data, status } = await api.userApi.getProfile();

        if (status === 200) {
          const user = data.data?.userInfo;
          const lineProfile = {
            displayName: user.lineInfo?.name,
            pictureUrl: user.lineInfo?.picture,
            userId: user.lineInfo?.sub,
          };
          setUserData({ ...user, line_data: lineProfile });
          return status;
        }
      } catch (error) {
        if (retryCount < maxRetries) {
          setTimeout(() => {
            getUser(retryCount + 1);
          }, retryDelay);
          return;
        }
        setIsReady(true);
        setUserData(null);
        console.error("getUser failed after all retries");
      } finally {
        if (retryCount === 0) {
          setHasInitialized(true);
        }
      }
    },
    [hasInitialized, setUserData]
  );

  const navigateLoginState = useCallback(() => {
    const fullPath = location.pathname + location.search;
    const loginType = pageParams.get("login_type");
    const isRegisterRoute = location.pathname.includes("/register");

    if (!userData?.id) {
      navigate(
        `${AUTH_ROUTES.REGISTER}${
          loginType ? `?${QUERY_PARAMS.LOGIN_TYPE}=${loginType}` : ""
        }`
      );
      return;
    }
    if ([0, 1, 3].includes(userData.register_state)) {
      if (penddingList.includes(location.pathname)) {
        return;
      }
      navigate(COMMON_ROUTES.PROFILE);
      return;
    }

    if (isRegisterRoute) {
      navigate(COMMON_ROUTES.PROFILE);
      return;
    }

    navigate(fullPath);
  }, [userData, location, pageParams, navigate]);
  useEffect(() => {
    if (userData) {
      let type = null;
      switch (userData.user_type) {
        case LOGIN_TYPES.CADDY:
          type = "caddy";
          break;
        case LOGIN_TYPES.GOLFER:
          type = "golfer";
          break;
        default:
          type = null;
          break;
      }
      setUserState({
        userType: type,
        showAuthRoutes: !userData.id,
      });
      setIsReady(true);
      navigateLoginState();
    }
  }, [userData]);

  useEffect(() => {
    if (!hasInitialized) {
      const initializeApp = async () => {
        await getUser();
      };
      if (isNotFoundUser) {
        setIsReady(true);
        navigateLoginState();
      }
      initializeApp();
    }
  }, [hasInitialized, getUser]);

  const authRoutes = (
    <>
      <Route
        path={AUTH_ROUTES.REGISTER}
        element={<Login is_comming={isDevelopment} is_caddy={isCaddy} />}
      />
      <Route path={AUTH_ROUTES.CADDY_REGISTER} element={<RegisterCaddy />} />
      <Route path={AUTH_ROUTES.GOLFER_REGISTER} element={<RegisterGolfer />} />
    </>
  );

  const caddyRoutes = (
    <>
      <Route
        path={CADDY_ROUTES.LEAVE_SETUP}
        element={<LeaveSetup is_comming={isDevelopment} />}
      />
      <Route
        path={CADDY_ROUTES.JOB_LIST}
        element={<CaddyJob is_comming={isDevelopment} />}
      />
      <Route path={CADDY_ROUTES.LUCKY_DRAW} element={<LuckyDraw />} />
      <Route
        path={CADDY_ROUTES.SCAN_QR}
        element={<CaddyJob is_comming={isDevelopment} />}
      />
      <Route path={CADDY_ROUTES.TIP_OFFER} element={<CaddyTipOffer />} />
      <Route
        path="*"
        element={<SmartRedirect defaultPath={CADDY_ROUTES.JOB_LIST} />}
      />
    </>
  );

  const golferRoutes = (
    <>
      <Route
        path={GOLFER_ROUTES.BOOK_QR}
        element={<CaddyJob tab={1} is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.BOOK_CADDY}
        element={<SearchCaddy is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.REVIEW}
        element={<Review is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.BOOKINGS}
        element={<CaddyJob is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.FAVORITE}
        element={<Favorite is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.BOOK_CADDY_WITH_ID}
        element={<SearchCaddyView is_comming={isDevelopment} />}
      />
      <Route
        path={GOLFER_ROUTES.BOOK_CADDY_WITH_BOOKING}
        element={<SearchCaddyView is_comming={isDevelopment} />}
      />
      <Route path={GOLFER_ROUTES.TIP_OFFER_LIST} element={<TipOfferList />} />
      <Route
        path={GOLFER_ROUTES.TIP_OFFER_CREATE}
        element={<TipOfferCreate />}
      />
      <Route
        path={GOLFER_ROUTES.TIP_OFFER_DETAIL}
        element={<TipOfferDetail />}
      />
      <Route
      />
      <Route
        path="*"
        element={<SmartRedirect defaultPath={GOLFER_ROUTES.BOOK_CADDY} />}
      />
    </>
  );

  if (!isReady) return <Loading visible={true} />;

  const userProfile = userData?.file_uploads?.find(
    (file) => file.type === "profile"
  );

  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/payment_fail" element={<></>} />
      <Route
        path={COMMON_ROUTES.PROFILE}
        element={
          <Profile
            user_type={userData?.user_type}
            user_profile={userProfile}
            user_data={userData}
          />
        }
      />
      {userState?.userType === "caddy"
        ? caddyRoutes
        : userState?.userType === "golfer"
        ? golferRoutes
        : authRoutes}
    </Routes>
  );
}
